/**
 * Phase 3.1 — Per-doctor segmentation helper for visit history.
 *
 * Splits a visit's metadata + sittings into per-attending-doctor
 * sub-segments using the `referral_history` chain. Single-doctor
 * visits return one segment (the original doctor) — callers can
 * decide to render the flat layout instead of segmented.
 *
 * A segment owns:
 *   - doctor_member_id, doctor_name (resolved best-effort from the visit row)
 *   - started_at, ended_at         (timestamps bracketing this doctor's window)
 *   - bypassed_receptionist        (true if reached this doctor via bypass)
 *   - reason                        (referral reason that brought them here)
 *
 * Entries are bucketed by `entry.created_at` falling within
 * `[started_at, ended_at)`. Entries with `added_by_member_id`
 * matching the segment doctor get explicit attribution; others land
 * in the segment whose time window contains them (defensive for
 * pre-3.1 historical visits where attribution wasn't denormed onto
 * every entry).
 */

/**
 * Build segments for a visit row.
 * @param {object} visit
 * @returns {{ segments: Array, isMultiDoctor: boolean }}
 */
export function groupVisitEntriesByAttendingDoctor(visit) {
  const history = visit?.referral_history || [];
  // Single-doctor visit — one segment, full visit window.
  if (!history.length) {
    return {
      isMultiDoctor: false,
      segments: [{
        doctor_member_id: visit?.doctor_member_id || null,
        doctor_name: visit?.doctor_name || null,
        started_at: visit?.created_at || null,
        ended_at: null,
        bypassed_receptionist: null,
        reason: null,
        is_first: true,
      }],
    };
  }

  // Multi-doctor: build segments anchored at each hop's `at` timestamp.
  // Segment 0 = original doctor from visit creation up to first hop.
  // Segment N = nth referred doctor from hop[N-1].at onwards.
  const segments = [];
  // Segment 0 — the visit's first attending doctor (the from_doctor of
  // the first hop, if present, else the visit's current doctor as a
  // best-effort fallback for malformed history).
  const firstFromId = history[0]?.from_doctor_id;
  segments.push({
    doctor_member_id: firstFromId || visit?.doctor_member_id || null,
    doctor_name: null,
    started_at: visit?.created_at || null,
    ended_at: history[0]?.at || null,
    bypassed_receptionist: null,
    reason: null,
    is_first: true,
  });
  for (let i = 0; i < history.length; i += 1) {
    const hop = history[i];
    const next = history[i + 1];
    segments.push({
      doctor_member_id: hop.to_resolved || hop.to_suggested,
      doctor_name: null,
      started_at: hop.at,
      ended_at: next ? next.at : null,
      bypassed_receptionist: !!hop.bypassed_receptionist,
      reason: hop.reason || null,
      is_first: false,
    });
  }
  return { isMultiDoctor: true, segments };
}

/**
 * Bucket a flat array of metadata entries into segments.
 * @param {Array} entries
 * @param {Array} segments — output of groupVisitEntriesByAttendingDoctor.
 * @returns {Array<Array>} — entries[i] for each segment[i].
 */
export function bucketEntriesIntoSegments(entries, segments) {
  if (!entries?.length || !segments?.length) {
    return segments.map(() => []);
  }
  const buckets = segments.map(() => []);

  // Time-window-first bucketing — the segment whose
  // [started_at, ended_at) contains entry.created_at wins. For pre-3.1
  // visits with no referral_history, this collapses to one bucket.
  for (const entry of entries) {
    const t = entry?.created_at;
    if (!t) {
      buckets[0].push(entry);
      continue;
    }
    let placed = false;
    for (let i = 0; i < segments.length; i += 1) {
      const seg = segments[i];
      const startOk = !seg.started_at || t >= seg.started_at;
      const endOk = !seg.ended_at || t < seg.ended_at;
      if (startOk && endOk) {
        buckets[i].push(entry);
        placed = true;
        break;
      }
    }
    if (!placed) buckets[buckets.length - 1].push(entry);
  }
  return buckets;
}

/**
 * Resolve a friendly display name for a segment's doctor by walking
 * the visit row's metadata + the entry author lookups.
 * Falls back to the doctor_member_id if no name is available.
 */
export function resolveSegmentDoctorName(segment, visit, entriesInSegment) {
  if (segment.doctor_name) return segment.doctor_name;
  // The visit row's doctor_name is the *current* primary doctor — only
  // useful for the last segment. Earlier segments fall back to the
  // member id.
  if (visit?.referral_history?.length) {
    const lastHop = visit.referral_history[visit.referral_history.length - 1];
    const isLastSegment = segment.doctor_member_id === (lastHop.to_resolved || lastHop.to_suggested);
    if (isLastSegment && visit.doctor_name) return `Dr. ${visit.doctor_name}`;
  } else if (visit?.doctor_name) {
    return `Dr. ${visit.doctor_name}`;
  }
  // Try to pluck a name from any entry attributed to this doctor.
  const sample = (entriesInSegment || []).find(
    (e) => e?.added_by_name && e?.added_by_member_id === segment.doctor_member_id,
  );
  if (sample) return sample.added_by_name;
  return segment.doctor_member_id ? `Dr. ${segment.doctor_member_id.slice(0, 6)}…` : 'Unknown';
}
